import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { usePaginatedApi, useApi } from '../hooks/useApi';
import { gpsAPI, systemConfigAPI } from '../services/api';
import GPSActivation from '../components/GPSActivation';
import Table from '../components/Table';
import Button from '../components/Button';
import LoadingSpinner from '../components/LoadingSpinner';
import { formatDateTime } from '../utils/helpers';
import {
  MapPinIcon,
  TruckIcon,
  ClockIcon,
  ArrowPathIcon,
  SignalIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const GPSPage = () => {
  const { t } = useTranslation();
  const { getDriverTruck } = useAuth();
  const [refreshKey, setRefreshKey] = useState(0);

  // Check if tracking module is enabled
  const { data: trackingConfig, loading: configLoading } = useApi(
    () => systemConfigAPI.getByKey('module.tracking.enabled'),
    [],
    { showErrorToast: false, immediate: true }
  );

  const isTrackingEnabled = trackingConfig?.value === true || trackingConfig?.value === 'true';

  const driverTruck = getDriverTruck();

  // Fetch GPS history for driver's truck
  const {
    data: gpsHistory,
    loading: historyLoading,
    pagination: historyPagination,
    refetch: refetchHistory,
    goToPage
  } = usePaginatedApi(
    driverTruck?.id ? () => gpsAPI.getTruckHistory(driverTruck.id, { limit: 20 }) : null,
    [driverTruck?.id, refreshKey],
    { immediate: !!driverTruck?.id }
  );

  // Auto-refresh GPS history every 30 seconds
  useEffect(() => {
    if (!driverTruck?.id) return;

    const interval = setInterval(() => {
      refetchHistory();
    }, 30000);

    return () => clearInterval(interval);
  }, [driverTruck?.id, refetchHistory]);

  const handleLocationUpdate = (location) => {
    // Refresh history when location is updated
    setRefreshKey(prev => prev + 1);
  };

  const handleRefreshHistory = () => {
    refetchHistory();
    setRefreshKey(prev => prev + 1);
  };

  const openGoogleMaps = (latitude, longitude) => {
    const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
    window.open(url, '_blank');
  };

  // Show loading while checking module status
  if (configLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Show disabled message if tracking module is not enabled
  if (!configLoading && !isTrackingEnabled) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <ExclamationTriangleIcon className="h-20 w-20 text-yellow-500 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('tracking.moduleDisabled')}</h2>
          <p className="text-lg text-gray-600 mb-6">{t('tracking.moduleDisabledDescription')}</p>
        </div>
      </div>
    );
  }

  const columns = [
    {
      key: 'timestamp',
      title: t('gps.columns.timestamp'),
      render: (value) => (
        <div className="text-sm">
          <div className="font-medium">{formatDateTime(value)}</div>
        </div>
      )
    },
    {
      key: 'location',
      title: t('gps.columns.location'),
      render: (_, row) => (
        <div className="text-sm">
          <button
            onClick={() => openGoogleMaps(row.latitude, row.longitude)}
            className="text-blue-600 hover:text-blue-800 hover:underline"
            title={t('gps.viewOnMap')}
          >
            {row.latitude.toFixed(6)}, {row.longitude.toFixed(6)}
          </button>
          {row.accuracy && (
            <div className="text-gray-500">±{Math.round(row.accuracy)}m</div>
          )}
        </div>
      )
    },
    {
      key: 'speed',
      title: t('gps.columns.speed'),
      render: (value) => (
        <div className="text-sm">
          {value ? (
            <span className={`px-2 py-1 rounded-full text-xs ${
              value > 50 ? 'bg-red-100 text-red-800' :
              value > 20 ? 'bg-yellow-100 text-yellow-800' :
              value > 0 ? 'bg-green-100 text-green-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {Math.round(value)} km/h
            </span>
          ) : (
            <span className="text-gray-500">-</span>
          )}
        </div>
      )
    },
    {
      key: 'status',
      title: t('gps.columns.status'),
      render: (value) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          value === 'IN_TRANSIT' ? 'bg-blue-100 text-blue-800' :
          value === 'PARKED' ? 'bg-gray-100 text-gray-800' :
          value === 'AT_PORT' ? 'bg-green-100 text-green-800' :
          value === 'LOADING' ? 'bg-yellow-100 text-yellow-800' :
          value === 'UNLOADING' ? 'bg-purple-100 text-purple-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {t(`gps.statuses.${value?.toLowerCase()}`)}
        </span>
      )
    },
    {
      key: 'batteryLevel',
      title: t('gps.columns.battery'),
      render: (value) => (
        <div className="text-sm">
          {value ? (
            <span className={`px-2 py-1 rounded text-xs ${
              value > 50 ? 'bg-green-100 text-green-800' :
              value > 20 ? 'bg-yellow-100 text-yellow-800' :
              'bg-red-100 text-red-800'
            }`}>
              {Math.round(value)}%
            </span>
          ) : (
            <span className="text-gray-500">-</span>
          )}
        </div>
      )
    }
  ];

  if (!driverTruck) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-3">
          <MapPinIcon className="h-8 w-8 text-red-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('gps.title')}</h1>
            <p className="text-gray-600">{t('gps.pageDescription')}</p>
          </div>
        </div>
        
        <div className="card">
          <div className="text-center py-8">
            <TruckIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('gps.noTruckAssigned')}</h3>
            <p className="text-gray-500">{t('gps.contactAdminForTruck')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <MapPinIcon className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('gps.title')}</h1>
            <p className="text-gray-600">{t('gps.pageDescription')}</p>
          </div>
        </div>
        
        <Button
          onClick={handleRefreshHistory}
          variant="outline"
          className="flex items-center space-x-2"
        >
          <ArrowPathIcon className="h-4 w-4" />
          <span>{t('gps.refresh')}</span>
        </Button>
      </div>

      {/* GPS Activation Widget */}
      <GPSActivation 
        truckId={driverTruck.id} 
        onLocationUpdate={handleLocationUpdate}
      />

      {/* GPS History */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">{t('gps.locationHistory')}</h2>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <ClockIcon className="h-4 w-4" />
            <span>{t('gps.autoRefresh30s')}</span>
          </div>
        </div>

        {historyLoading ? (
          <div className="py-8">
            <LoadingSpinner />
          </div>
        ) : gpsHistory?.length > 0 ? (
          <Table
            columns={columns}
            data={gpsHistory}
            pagination={{
              page: historyPagination.page,
              limit: historyPagination.limit,
              total: historyPagination.total,
              totalPages: historyPagination.totalPages,
              onChange: goToPage
            }}
          />
        ) : (
          <div className="text-center py-8">
            <SignalIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('gps.noLocationHistory')}</h3>
            <p className="text-gray-500 mb-4">{t('gps.startTrackingToSeeHistory')}</p>
          </div>
        )}
      </div>

      {/* GPS Status Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <TruckIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">{t('gps.assignedTruck')}</p>
              <p className="text-lg font-semibold text-gray-900">{driverTruck.plateNumber}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <MapPinIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">{t('gps.totalLocations')}</p>
              <p className="text-lg font-semibold text-gray-900">
                {historyPagination?.total || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <ClockIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">{t('gps.lastSeen')}</p>
              <p className="text-lg font-semibold text-gray-900">
                {gpsHistory?.[0]?.timestamp ? 
                  formatDateTime(gpsHistory[0].timestamp) : 
                  t('gps.never')
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* GPS Usage Instructions */}
      <div className="card bg-blue-50 border-blue-200">
        <div className="flex items-start space-x-3">
          <ExclamationTriangleIcon className="h-6 w-6 text-blue-600 mt-1" />
          <div>
            <h3 className="text-lg font-medium text-blue-900 mb-2">{t('gps.instructions.title')}</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• {t('gps.instructions.step1')}</li>
              <li>• {t('gps.instructions.step2')}</li>
              <li>• {t('gps.instructions.step3')}</li>
              <li>• {t('gps.instructions.step4')}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GPSPage;
